import React from 'react';
import { navigate } from 'gatsby';
import styled from 'styled-components';
import {
  breakpointSizes,
  minBreakpointQuery,
  sectionMargins,
  standardColours,
  brandColours,
  brandFonts,
  fontSize,
  fluidFontSize,
} from '../styles';
import { Container, Heading, Svg, Image, Link } from './ui';
import flowerEyeIcon from '../images/flower-eye-icon.svg';

const StyledPopularSpas = styled.section`
  ${sectionMargins()};
`;

const StyledHeader = styled.header`
  margin: 0 auto 30px;
  max-width: 660px;
  text-align: center;

  ${minBreakpointQuery.small`
    margin-bottom: 40px;
  `}

  ${minBreakpointQuery.medium`
    margin-bottom: 50px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 60px;
  `}
`;

const StyledIcon = styled(Svg)`
  margin-bottom: 6px;
  width: 75px;
  fill: ${brandColours.primary};
`;

const StyledItems = styled.div`
  display: grid;
  gap: 20px;

  ${minBreakpointQuery.tsmall`
    grid-template-columns: repeat(2, 1fr);
    row-gap: 30px;
  `}

  ${minBreakpointQuery.medium`
    grid-template-columns: repeat(3, 1fr);
    row-gap: 40px;
  `}

  ${minBreakpointQuery.large`
    row-gap: 50px;
  `}
`;

const StyledItem = styled.article`
  position: relative;
  cursor: pointer;

  .gatsby-image-wrapper {
    position: relative;
    display: block;

    &:after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: linear-gradient(
        to top,
        ${standardColours.black} 0%,
        ${standardColours.transparent} 100%
      );
      opacity: 0.8;
    }
  }
`;

const StyledContent = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
  padding: 25px 20px;
  color: ${standardColours.white};

  ${minBreakpointQuery.tiny`
    padding: 30px;
  `}

  ${minBreakpointQuery.smedium`
    padding 40px;
  `}
`;

const StyledOverline = styled.p`
  color: ${brandColours.quaternary};
  ${fontSize(12)}

  ${minBreakpointQuery.small`
    ${fontSize(14)}
  `}

  ${minBreakpointQuery.large`
    ${fontSize(16)}
  `}
`;

const StyledHeading = styled.h3`
  margin-top: 6px;
  ${brandFonts.secondary};
  ${fluidFontSize(
    '23px',
    '29px',
    breakpointSizes.tiny,
    breakpointSizes.xxlarge
  )};
  line-height: 1.1;

  ${minBreakpointQuery.small`
    margin-top: 8px;
  `}
`;

const StyledText = styled.p`
  margin-top: 4px;
  ${fontSize(12)}

  ${minBreakpointQuery.small`
    margin-top: 6px;
    ${fontSize(14)}
  `}

  ${minBreakpointQuery.large`
    ${fontSize(16)}
  `}
`;

const PopularSpas = ({ heading, items }) =>
  items.length > 0 && (
    <StyledPopularSpas>
      <Container>
        <StyledHeader>
          <StyledIcon image={flowerEyeIcon} />
          <Heading>{heading}</Heading>
        </StyledHeader>
        <StyledItems>
          {items.map(({ title, subtitle, image, url }, id) => (
            <StyledItem
              key={id}
              onClick={() => {
                navigate(url);
              }}
            >
              <Image
                src={image.url}
                alt={image.title}
                width={480}
                height={550}
                layout="constrained"
              />
              <StyledContent>
                <StyledOverline>Most popular</StyledOverline>
                <StyledHeading>
                  <Link to={url}>{title}</Link>
                </StyledHeading>
                <StyledText>{subtitle}</StyledText>
              </StyledContent>
            </StyledItem>
          ))}
        </StyledItems>
      </Container>
    </StyledPopularSpas>
  );

export default PopularSpas;
